// packages
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Eye, Loader2 } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import { addHours } from 'date-fns'

// components
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CustomDataTable } from '@/components/table/custom-data-table'
import { PageHeader } from '@/components/common/page-header'
import { ResultDialog } from '@/components/signs-symptoms/result-dialog'

// libs
import { showConfirm } from '@/lib/alerts'
import { cn } from '@/lib/utils'

// hooks
import { useToast } from '@/hooks/use-toast'

// store
import { useSignsSymptomsStore } from '@/store/signs-symptoms'

// entities
import { SignsSymptomProps, formatted_signs_symptom_category, countCheckedSymptoms, formatted_signs_symptom_status, signs_symptom_status } from '@/entities/signs-symptom'

// utils
import { formatDate } from '@/utils/dates-util'

const loc = '@/pages/SignsSymptoms'

const STATUS_BADGE_CLASS: Record<signs_symptom_status, string> = {
  [signs_symptom_status.DRAFT]: 'bg-muted text-muted-foreground',
  [signs_symptom_status.PROCESSING]: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
  [signs_symptom_status.ACTIVE]: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
  [signs_symptom_status.FAILED]: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  [signs_symptom_status.DELETED]: 'bg-muted text-muted-foreground'
}

export default function SignsSymptoms() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const items = useSignsSymptomsStore(s => s.items)
  const fetchAll = useSignsSymptomsStore(s => s.fetchAll)
  const fetchByRef = useSignsSymptomsStore(s => s.fetchByRef)
  const remove = useSignsSymptomsStore(s => s.remove)

  const [previewRecord, setPreviewRecord] = useState<SignsSymptomProps | null>(null)

  useEffect(() => {
    fetchAll()
  }, [])

  // Polling fallback: a cada 30s, refresha os PROCESSING para o caso de o Ably falhar.
  useEffect(() => {
    const hasProcessing = items.some(i => i.status === signs_symptom_status.PROCESSING)
    if (!hasProcessing) return
    const id = setInterval(() => {
      items.filter(i => i.status === signs_symptom_status.PROCESSING).forEach(i => fetchByRef(i.ref))
    }, 30000)
    return () => clearInterval(id)
  }, [items])

  const _handleDelete = async (record: SignsSymptomProps) => {
    const fullLoc = `${loc}._handleDelete`
    try {
      const { isConfirmed } = await showConfirm(`Deseja remover o registro de "${record.patientName}"?`)
      if (!isConfirmed) return
      const ok = await remove(record.ref)
      if (ok) toast({ variant: 'success', title: 'Sucesso', description: 'Registro removido com sucesso.' })
      else toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível remover o registro.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível remover o registro.' })
      console.error(`Unhandled error at ${fullLoc}. Details: ${err}`)
    }
  }

  const columns: ColumnDef<SignsSymptomProps>[] = [
    {
      accessorKey: 'patientName',
      header: 'Paciente',
      cell: ({ row }) => {
        const symptomCount = countCheckedSymptoms(row.original)
        return (
          <div className="flex flex-col">
            <span className="text-foreground font-semibold">{row.original.patientName}</span>
            <span className="text-xs text-muted-foreground">
              {symptomCount} sintoma{symptomCount !== 1 ? 's' : ''} marcado{symptomCount !== 1 ? 's' : ''}
            </span>
          </div>
        )
      }
    },
    {
      accessorKey: 'category',
      header: 'Categoria',
      cell: ({ row }) => (
        <span className="rounded-md py-1 px-2 text-center text-xs font-semibold bg-primary/15 text-coentro">
          {formatted_signs_symptom_category[row.original.category]}
        </span>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = row.original.status
        return (
          <span className={cn('inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider', STATUS_BADGE_CLASS[s])}>
            {s === signs_symptom_status.PROCESSING && <Loader2 className="h-3 w-3 animate-spin" />}
            {formatted_signs_symptom_status[s]}
          </span>
        )
      }
    },
    {
      id: 'aiSummary',
      header: 'Resumo IA',
      cell: ({ row }) => {
        if (row.original.status === signs_symptom_status.PROCESSING)
          return <span className="text-xs text-amber-700 italic">Aguardando análise da Nina...</span>
        if (row.original.status === signs_symptom_status.FAILED)
          return <span className="text-xs text-red-700 italic">Falha na geração</span>
        if (row.original.status !== signs_symptom_status.ACTIVE)
          return <span className="text-xs text-muted-foreground italic">Análise não gerada</span>
        return <p className="text-sm text-foreground/85 line-clamp-1 max-w-xs">{row.original.summary || 'Análise gerada.'}</p>
      }
    },
    {
      accessorKey: 'updatedAt',
      header: 'Atualizado',
      cell: ({ row }) => {
        const dt = new Date(row.original.updatedAt || row.original.createdAt)
        return <span className="text-foreground whitespace-nowrap">{formatDate(addHours(dt, 3))}</span>
      }
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const item = row.original
        const isActive = item.status === signs_symptom_status.ACTIVE
        const isProcessing = item.status === signs_symptom_status.PROCESSING
        return (
          <div className="flex justify-end gap-x-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => setPreviewRecord(item)} disabled={!isActive}>
                  <span className="sr-only">Visualizar</span>
                  <Eye className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                {isActive ? 'Visualizar análise' : 'Análise indisponível'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="default" size="sm" onClick={() => navigate(`/signs-symptoms/${item.ref}`)} disabled={isProcessing}>
                  <span className="sr-only">Editar</span>
                  <Pencil className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                {isProcessing ? 'Aguarde a análise concluir' : 'Editar'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="default" size="sm" onClick={() => _handleDelete(item)} className="bg-red-700 text-white hover:bg-red-700/70 hover:brightness-125" disabled={isProcessing}>
                  <span className="sr-only">Deletar</span>
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-red-700 text-white text-xs">
                Deletar
              </TooltipContent>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  return (
    <>
      <div className="mx-0">
        <PageHeader title="Sinais e Sintomas" description="Mapeie sinais e sintomas da paciente e gere análise clínica com apoio da Nina" />

        <div className="rounded-xl shadow-sm mt-5">
          <CustomDataTable columns={columns} data={items} filterKey={{ title: 'Paciente', value: 'patientName' }} amount={items.length}>
            <div className="w-full flex flex-row gap-y-2 space-between items-end smAndDown:flex-col smAndDown:gap-y-4 smAndDown:items-center">
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="default" onClick={() => navigate('/signs-symptoms/new')} className="smAndDown:w-full">
                      <Plus className="size-5" />
                      Novo registro
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs mdAndUp:hidden">
                    Novo registro
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </CustomDataTable>
        </div>
      </div>

      <ResultDialog open={!!previewRecord} record={previewRecord} onClose={() => setPreviewRecord(null)} />
    </>
  )
}
